import { FC, useState, useEffect } from "react";
import { Card, Row, Col, Statistic, Spin } from "antd";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { fetchEventFrequencyData, fetchOverviewData, fetchTrendData } from "../actions/overviewActions";
import { EventFrequencyData, OverviewData, TrendData } from "../types/overview";
import { COLOR_MAPPING } from "../utils/color";
import { useFilters } from "../hooks/useFilters";
import AnalyticsFilters from "../components/AnalyticsFilters";

const Overview: FC = () => {
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [trendData, setTrendData] = useState<TrendData | null>(null);
  const [eventFrequencyData, setEventFrequencyData] = useState<EventFrequencyData | null>(null);

  const { filters, filterOptions, handleDateRangeChange, handleFilterChange } = useFilters();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams();
        filters.start_date && params.set("start_date", filters.start_date);
        filters.end_date && params.set("end_date", filters.end_date);
        filters.country && params.set("country", filters.country);
        filters.browser && params.set("browser", filters.browser);

        const [overviewRes, trendsRes, eventFreqRes] = await Promise.all([
          fetchOverviewData(filters),
          fetchTrendData(filters),
          fetchEventFrequencyData(filters),
        ]);

        const overview = await overviewRes;
        const trends = await trendsRes;
        const eventFreq = await eventFreqRes;

        setOverviewData(overview);
        setTrendData(trends);
        setEventFrequencyData(eventFreq);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  if (loading && !overviewData) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <AnalyticsFilters
        filters={filters}
        filterOptions={filterOptions}
        onDateRangeChange={handleDateRangeChange}
        onFilterChange={handleFilterChange}
        loading={loading}
        showEmailDomain={true}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Total Events" value={overviewData?.total_events || 0} loading={loading} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Active Users" value={overviewData?.active_users || 0} loading={loading} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="Avg Events/User"
              value={overviewData?.avg_events_per_user || 0}
              precision={2}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card>
            <Statistic
              title="Activation Rate"
              value={overviewData?.activation_rate || 0}
              precision={2}
              suffix="%"
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card>
            <Statistic
              title="7-Day Retention"
              value={overviewData?.retention_7day || 0}
              precision={2}
              suffix="%"
              loading={loading}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Daily Active Users" loading={loading}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData?.dau_trend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke={COLOR_MAPPING.blue} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Events per Day" loading={loading}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData?.events_trend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke={COLOR_MAPPING.green} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Top 5 Events" loading={loading}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={eventFrequencyData?.top_events || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="event" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill={COLOR_MAPPING.orange} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Overview;
